"use client";

import {useTranslation} from "react-i18next";
import {
   CodeBlock,
   CodeBlockActions,
   CodeBlockCopyButton,
   CodeBlockHeader,
   CodeBlockTitle,
} from "@/components/ai-elements/code-block";
import type {HighlightLanguage} from "@/components/ai-elements/code-block-highlight";
import {type StepCodeLanguage} from "../toolCallUtils";
import ToggleChevron from "./ToggleChevron";
import {type LineExpansion} from "./types";

const CODE_BLOCK_LANGUAGES: Record<StepCodeLanguage, HighlightLanguage> = {
   python: "python",
   bash: "bash",
   json: "json",
   // Plaintext — no dedicated grammar for arbitrary execute_code languages.
   text: "text",
};

type StepCodeBlockProps = Readonly<{
   code: string;
   visibleCode: string;
   codeLanguage: StepCodeLanguage;
   languageLabel: string;
   lineCount: number;
   expansion: LineExpansion;
}>;

export default function StepCodeBlock({
   code,
   visibleCode,
   codeLanguage,
   languageLabel,
   lineCount,
   expansion,
}: StepCodeBlockProps) {
   const {t} = useTranslation("ask");
   const {hiddenCount, showAll, onToggleShowAll} = expansion;

   const footer =
      hiddenCount > 0 ? (
         <button
            type="button"
            onClick={onToggleShowAll}
            className="flex w-full items-center gap-1.5 border-t border-neutral/50 px-2.5 py-1.5 text-left text-xs text-400 transition-colors hover:text-600"
         >
            <ToggleChevron expanded={showAll} />
            {showAll ? t("tools.labels.showLess") : t("tools.labels.moreLines", {count: hiddenCount})}
         </button>
      ) : undefined;

   return (
      <CodeBlock
         className="border-neutral/60 bg-surface shadow-none"
         code={visibleCode}
         copyText={code}
         language={CODE_BLOCK_LANGUAGES[codeLanguage]}
         footer={footer}
      >
         <CodeBlockHeader className="border-neutral/50 bg-100/40">
            <CodeBlockTitle>
               <span className="font-mono">{languageLabel}</span>
               <span className="opacity-50">·</span>
               <span>{t("tools.labels.lineCount", {count: lineCount})}</span>
            </CodeBlockTitle>
            <CodeBlockActions>
               <CodeBlockCopyButton className="h-6 w-6" />
            </CodeBlockActions>
         </CodeBlockHeader>
      </CodeBlock>
   );
}
